// src/components/AGTFit/service/agtfitService.js
import axios from 'axios';
import { getAuth } from 'firebase/auth';

// Configuración base para las peticiones al API según el entorno
const API_BASE = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5003'
  : (process.env.REACT_APP_API_FIT_URL);

// Prefijo de API según entorno
const API_PREFIX = process.env.NODE_ENV === 'development' ? '/api' : '';

// URL completa
const API_URL = `${API_BASE}${API_PREFIX}`;

// Función para limitar la frecuencia de llamadas a una función
const throttle = (func, limit) => {
  let inThrottle;
  let lastResult;
  
  return function() {
    const args = arguments;
    const context = this;
    
    if (!inThrottle) {
      inThrottle = true;
      lastResult = func.apply(context, args);
      
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
    
    return lastResult;
  };
};

// Sistema de caché mejorado con tiempos de expiración y control de frecuencia
const apiCache = {
  // Datos en caché
  fitProfile: null,
  checkIns: null,
  goal: null,
  
  // Tiempos de última actualización
  lastFetch: {
    fitProfile: null,
    checkIns: null,
    goal: null,
    stats: {},
    dailyStats: {},
    meals: {},
    exercises: {}
  },
  
  // Control de intentos de solicitud
  requestAttempts: {
    fitProfile: null,
    checkIns: null,
    goal: null
  },
  
  // Promesas pendientes para consolidar solicitudes simultáneas
  pendingPromises: {},
  
  // Contadores de errores para backoff exponencial
  errorCount: {}
};

// Tiempos de expiración de caché (en milisegundos)
const CACHE_EXPIRY = {
  fitProfile: 5 * 60 * 1000, // 5 minutos
  checkIns: 10 * 60 * 1000, // 10 minutos
  goal: 10 * 60 * 1000, // 10 minutos
  stats: 2 * 60 * 1000, // 2 minutos
  dailyStats: 1 * 60 * 1000, // 1 minuto
  meals: 2 * 60 * 1000, // 2 minutos
  favorites: 15 * 60 * 1000, // 15 minutos
  history: 5 * 60 * 1000, // 5 minutos
  userFoods: 15 * 60 * 1000, // 15 minutos
  routines: 10 * 60 * 1000, // 10 minutos
  exercises: 2 * 60 * 1000 // 2 minutos
};

// Tiempos mínimos entre solicitudes (throttling, en milisegundos)
const MIN_REQUEST_INTERVAL = {
  fitProfile: 10000, // 10 segundos
  checkIns: 5000, // 5 segundos
  goal: 5000, // 5 segundos
  stats: 10000, // 10 segundos
  dailyStats: 10000, // 10 segundos
  meals: 5000, // 5 segundos
  favorites: 11000, // 11 segundos
  history: 5000, // 5 segundos
  userFoods: 9000, // 9 segundos
  routines: 5000, // 5 segundos
  exercises: 3000 // 3 segundos
};

// Función para verificar si la caché está vigente
const isCacheValid = (cacheType, specificKey = null) => {
  // const key = specificKey ? `${cacheType}_${specificKey}` : cacheType; // Unused variable
  const lastFetch = specificKey ? apiCache.lastFetch[cacheType]?.[specificKey] : apiCache.lastFetch[cacheType];
  const expiryTime = CACHE_EXPIRY[cacheType];

  return lastFetch && (Date.now() - lastFetch < expiryTime);
};

// Función para verificar si se puede realizar una nueva solicitud (throttling)
const canMakeRequest = (cacheType, specificKey = null) => {
  // const key = specificKey ? `${cacheType}_${specificKey}` : cacheType; // Unused variable
  const lastFetch = specificKey ? apiCache.lastFetch[cacheType]?.[specificKey] : apiCache.lastFetch[cacheType];
  const minInterval = MIN_REQUEST_INTERVAL[cacheType];

  return !lastFetch || (Date.now() - lastFetch > minInterval);
};

// Función para calcular tiempo de espera con backoff exponencial
const calculateBackoffTime = (errorCount, baseTime = 1000, maxTime = 30000) => {
  const backoffTime = Math.min(baseTime * Math.pow(2, errorCount), maxTime);
  // Añadir un poco de aleatoriedad (jitter) para evitar que todas las solicitudes se sincronicen
  return backoffTime + Math.random() * 1000;
};

// Modificar la configuración de axios
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
  timeout: 30000 // 30 segundos
});

// Interceptor para añadir el token de autenticación de Firebase
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (user) {
        const token = await user.getIdToken();
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error al obtener token de Firebase:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor de respuesta mejorado con manejo de errores 429
apiClient.interceptors.response.use(
  (response) => {
    // Resetear contador de errores para esta URL al tener éxito
    const url = response.config.url;
    if (apiCache.errorCount[url]) {
      apiCache.errorCount[url] = 0;
    }
    return response;
  },
  async (error) => {
    // No loggear errores 404 para el endpoint de perfil ya que es un comportamiento esperado
    if (!(error.config && 
          error.config.url && 
          error.config.url.includes('/fit-profile') && 
          error.response && 
          error.response.status === 404)) {
      console.error(`Error en respuesta:`, error.message);
    }
    
    // Manejar error 429 (Too Many Requests) con reintento automático
    if (error.response && error.response.status === 429 && error.config) {
      const url = error.config.url;
      
      // Incrementar contador de errores para esta URL
      apiCache.errorCount[url] = (apiCache.errorCount[url] || 0) + 1;
      
      // Calcular tiempo de espera con backoff exponencial
      const retryAfter = error.response.headers['retry-after'] 
        ? parseInt(error.response.headers['retry-after']) * 1000 
        : calculateBackoffTime(apiCache.errorCount[url]);
      
      console.warn(`Error 429 detectado. Reintentando en ${retryAfter/1000} segundos.`);
      
      // Esperar y reintentar
      await new Promise(resolve => setTimeout(resolve, retryAfter));
      return apiClient(error.config);
    }
    
    return Promise.reject(error);
  }
);

// Estados de perfil para el usuario
export const PROFILE_STATUS = {
  NONE: 'none',                    // No tiene perfil
  PENDING: 'pending_verification', // Perfil creado pero pendiente de verificación
  ACTIVE: 'active',                // Perfil activo y verificado
  INACTIVE: 'inactive'             // Perfil inactivo o suspendido
};

// Servicio para comunicación con el backend de AGT Fit
const agtfitService = {
  // Variable para controlar solicitudes simultáneas de actualización de calorías
  _resetCaloriesInProgress: false,

  /**
   * Obtiene el perfil del usuario con control de frecuencia y caché mejorada
   * @param {boolean} forceReload Forzar recarga desde el servidor ignorando caché
   * @param {boolean} noCache No usar caché aunque esté disponible
   * @returns {Promise<Object>} Respuesta con el perfil
   */
  getProfile: async (forceReload = false, noCache = false) => {
    try {
      const now = Date.now();
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises.profile && !forceReload) {
        return apiCache.pendingPromises.profile;
      }
      
      // Si no se solicita forzar recarga, verificar caché
      if (!forceReload && !noCache) {
        // Verificar si tenemos caché válida
        if (apiCache.fitProfile && isCacheValid('fitProfile')) {
          return { success: true, data: apiCache.fitProfile };
        }
        
        // Verificar si hay caché en localStorage
        const cachedProfile = localStorage.getItem('agtfit_profile');
        if (cachedProfile) {
          try {
            const parsed = JSON.parse(cachedProfile);
            apiCache.fitProfile = parsed;
            apiCache.lastFetch.fitProfile = now;
            return { success: true, data: parsed, fromCache: true };
          } catch (e) {
            console.error('Error al parsear perfil en caché:', e);
          }
        }
        
        // Verificar si ya hemos intentado y recibido un 404 recientemente
        if (apiCache.requestAttempts.fitProfile && (now - apiCache.requestAttempts.fitProfile < 60000)) {
          return { success: false, error: "No profile found", data: null };
        }
        
        // Verificar si podemos hacer una nueva solicitud (throttling)
        if (!canMakeRequest('fitProfile')) {
          // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
          if (apiCache.fitProfile) {
            return { success: true, data: apiCache.fitProfile, fromCache: true };
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const profilePromise = (async () => {
        try {
          const response = await apiClient.get('/fit-profile');
          
          // Actualizar caché
          if (response.data.success && response.data.data) {
            apiCache.fitProfile = response.data.data;
            apiCache.lastFetch.fitProfile = now;
            
            // Guardar en localStorage como backup
            localStorage.setItem('agtfit_profile', JSON.stringify(response.data.data));
          }
          
          return response.data;
        } catch (error) {
          // Registrar el intento fallido
          apiCache.requestAttempts.fitProfile = now;
          
          // Si es 404, no es realmente un error, solo significa que no hay perfil
          if (error.response && error.response.status === 404) {
            return { 
              success: false, 
              error: "No profile found",
              data: null,
              status: 404
            };
          }
          
          console.error('Error al obtener perfil de fitness:', error);
          
          // Intentar usar caché local si hay error
          const cachedProfile = localStorage.getItem('agtfit_profile');
          if (cachedProfile) {
            try {
              const parsed = JSON.parse(cachedProfile);
              return { success: true, data: parsed, fromCache: true };
            } catch (e) {
              console.error('Error al parsear perfil en caché:', e);
            }
          }
          
          return { 
            success: false, 
            error: error.message,
            data: null 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises.profile;
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises.profile = profilePromise;
      
      return profilePromise;
    } catch (error) {
      console.error('Error inesperado en getProfile:', error);
      return { 
        success: false, 
        error: error.message,
        data: null 
      };
    }
  },

  /**
   * Crea un perfil de fitness con manejo de errores mejorado
   * @param {Object} profileData Datos del perfil
   * @returns {Promise<Object>} Respuesta con el perfil creado
   */
  createProfile: async (profileData) => {
    try {
      // Guardar datos del perfil en localStorage primero como respaldo
      localStorage.setItem('agtfit_profile', JSON.stringify(profileData));
      
      // Reiniciar el indicador de intento fallido
      apiCache.requestAttempts.fitProfile = null;
      
      const response = await apiClient.post('/fit-profile', profileData);
      
      // Actualizar caché y localStorage
      if (response.data.success && response.data.data) {
        apiCache.fitProfile = response.data.data;
        apiCache.lastFetch.fitProfile = Date.now();
        localStorage.setItem('agtfit_profile', JSON.stringify(response.data.data));
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al crear perfil de fitness:', error);
      
      // En caso de error, mantener los datos en localStorage
      return { 
        success: false, 
        data: profileData, // Devolver los datos originales
        error: error.message,
        fromLocalStorage: true
      };
    }
  },

  /**
   * Actualiza un perfil de fitness existente con manejo de errores mejorado
   * @param {Object} profileData Datos actualizados del perfil
   * @returns {Promise<Object>} Respuesta con el perfil actualizado
   */
  updateProfile: async (profileData) => {
    try {
      // Guardar datos en localStorage primero como respaldo
      localStorage.setItem('agtfit_profile', JSON.stringify({
        ...apiCache.fitProfile,
        ...profileData
      }));
  
      const response = await apiClient.put('/fit-profile', profileData);
  
      // Actualizar caché y localStorage
      if (response.data.success && response.data.data) {
        apiCache.fitProfile = response.data.data;
        apiCache.lastFetch.fitProfile = Date.now();
        localStorage.setItem('agtfit_profile', JSON.stringify(response.data.data));
  
        // Invalidar también caché de estadísticas para forzar recarga
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('stats_')) {
            sessionStorage.removeItem(key);
          }
        });
        
        // Limpiar caché de estadísticas
        apiCache.lastFetch.stats = {};
      }
  
      return response.data;
    } catch (error) {
      console.error('Error al actualizar perfil de fitness:', error);
  
      // En caso de error, devolver los datos del localStorage
      return { 
        success: false, 
        data: JSON.parse(localStorage.getItem('agtfit_profile')), 
        error: error.message,
        fromLocalStorage: true
      };
    }
  },
  
  // Añadir un nuevo método para recalcular macros basado en calorías
  recalculateMacros: (calories, dietType = 'recommended') => {
    // Distribución de macros según tipo de dieta
    let proteinPercentage, carbsPercentage, fatsPercentage;
  
    switch (dietType) {
      case 'low-carb':
        proteinPercentage = 0.35; // 35%
        fatsPercentage = 0.45;    // 45%
        carbsPercentage = 0.20;   // 20%
        break;
      case 'high-protein':
        proteinPercentage = 0.40; // 40%
        carbsPercentage = 0.35;   // 35%
        fatsPercentage = 0.25;    // 25%
        break;
      case 'balanced':
      case 'recommended':
      default:
        proteinPercentage = 0.30; // 30%
        carbsPercentage = 0.45;   // 45%
        fatsPercentage = 0.25;    // 25%
        break;
    }
  
    // Calorías por gramo de cada macronutriente
    const caloriesPerGramProtein = 4;
    const caloriesPerGramCarbs = 4;
    const caloriesPerGramFats = 9;
  
    // Cálculo de gramos según porcentaje de calorías
    const proteinCalories = calories * proteinPercentage;
    const carbsCalories = calories * carbsPercentage;
    const fatsCalories = calories * fatsPercentage;
  
    // Convertir calorías a gramos
    const proteinGrams = Math.round(proteinCalories / caloriesPerGramProtein);
    const carbsGrams = Math.round(carbsCalories / caloriesPerGramCarbs);
    const fatsGrams = Math.round(fatsCalories / caloriesPerGramFats);
  
    return {
      protein: proteinGrams,
      carbs: carbsGrams,
      fats: fatsGrams
    };
  },

  /**
   * Obtiene comidas de un período específico con control de caché y frecuencia
   * @param {string} date Fecha en formato YYYY-MM-DD
   * @param {string} period Período ('day', 'week', 'month', etc.)
   * @returns {Promise<Object>} Respuesta con las comidas
   */
  getMeals: async (date = new Date().toISOString().split('T')[0], period = 'week') => {
    try {
      const now = Date.now();
      const cacheKey = `${date}_${period}`;
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`meals_${cacheKey}`]) {
        return apiCache.pendingPromises[`meals_${cacheKey}`];
      }
      
      // Para períodos mayores a un día, necesitamos obtener datos de múltiples días
      let startDate = date;
      const endDate = date;
  
      // Calcular la fecha de inicio según el período
      if (period === 'week' || period === '7D') {
        const weekStart = new Date(date);
        weekStart.setDate(weekStart.getDate() - 6); // 6 días atrás
        startDate = weekStart.toISOString().split('T')[0];
      } else if (period === 'month' || period === '1M') {
        const monthStart = new Date(date);
        monthStart.setDate(1); // Primer día del mes
        startDate = monthStart.toISOString().split('T')[0];
      } else if (period === '3month' || period === '3M') {
        const threeMonthsAgo = new Date(date);
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 2);
        threeMonthsAgo.setDate(1);
        startDate = threeMonthsAgo.toISOString().split('T')[0];
      } else if (period === 'year' || period === '1Y') {
        const yearStart = new Date(date);
        yearStart.setMonth(0);
        yearStart.setDate(1);
        startDate = yearStart.toISOString().split('T')[0];
      } else if (period === 'max') {
        startDate = "2020-01-01"; // Una fecha muy anterior
      }
      
      // Verificar si tenemos caché válida
      if (!apiCache.lastFetch.meals) {
        apiCache.lastFetch.meals = {};
      }
      
      if (isCacheValid('meals', cacheKey)) {
        const cachedMeals = sessionStorage.getItem(`meals_${cacheKey}`);
        if (cachedMeals) {
          try {
            return JSON.parse(cachedMeals);
          } catch (e) {
            console.error('Error al parsear comidas en caché:', e);
          }
        }
      }
      
      // Verificar si podemos hacer una nueva solicitud (throttling)
      if (!canMakeRequest('meals', cacheKey)) {
        // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
        const cachedMeals = sessionStorage.getItem(`meals_${cacheKey}`);
        if (cachedMeals) {
          try {
            return JSON.parse(cachedMeals);
          } catch (e) {
            console.error('Error al parsear comidas en caché:', e);
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const mealsPromise = (async () => {
        try {
          
          // Modificar para obtener datos de un rango de fechas
          const response = await apiClient.get(`/fit-meals?startDate=${startDate}&endDate=${endDate}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`meals_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.meals[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener comidas:', error);
          
          // Intentar usar caché si hay error
          const cachedMeals = sessionStorage.getItem(`meals_${cacheKey}`);
          if (cachedMeals) {
            try {
              return JSON.parse(cachedMeals);
            } catch (e) {
              console.error('Error al parsear comidas en caché:', e);
            }
          }
          
          return { success: false, data: [], error: error.message };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`meals_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`meals_${cacheKey}`] = mealsPromise;
      
      return mealsPromise;
    } catch (error) {
      console.error('Error inesperado en getMeals:', error);
      return { success: false, data: [], error: error.message };
    }
  },
 
  /**
   * Registra una comida con manejo de errores mejorado
   * @param {Object} mealData Datos de la comida
   * @returns {Promise<Object>} Respuesta con la comida registrada
   */
  logMeal: async (mealData) => {
    try {
      const response = await apiClient.post('/fit-meals', mealData);
      
      // Invalidar caché de comidas y estadísticas
      const date = mealData.date || new Date().toISOString().split('T')[0];
      
      // Limpiar caché de comidas
      if (!apiCache.lastFetch.meals) {
        apiCache.lastFetch.meals = {};
      }
      
      // Invalidar todas las cachés de comidas relacionadas con la fecha
      Object.keys(apiCache.lastFetch.meals).forEach(key => {
        if (key.includes(date)) {
          delete apiCache.lastFetch.meals[key];
          sessionStorage.removeItem(`meals_${key}`);
        }
      });
      
      // Limpiar caché de estadísticas
      if (!apiCache.lastFetch.stats) {
        apiCache.lastFetch.stats = {};
      }
      
      // Invalidar todas las cachés de estadísticas relacionadas con la fecha
      Object.keys(apiCache.lastFetch.stats).forEach(key => {
        if (key.includes(date)) {
          delete apiCache.lastFetch.stats[key];
          sessionStorage.removeItem(`stats_${key}`);
        }
      });

      // Invalidar caché de estadísticas diarias
      if (apiCache.lastFetch.dailyStats && apiCache.lastFetch.dailyStats[date]) {
        delete apiCache.lastFetch.dailyStats[date];
        sessionStorage.removeItem(`daily_${date}`);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al registrar comida:', error);
      return { success: false, data: null, error: error.message };
    }
  },
 
  /**
   * Obtiene estadísticas con control de caché y frecuencia
   * @param {string} period Período ('day', 'week', 'month', etc.)
   * @param {string} date Fecha en formato YYYY-MM-DD
   * @returns {Promise<Object>} Respuesta con las estadísticas
   */
  getStats: async (period = 'day', date = new Date().toISOString().split('T')[0]) => {
    try {
      const now = Date.now();
      const cacheKey = `${period}_${date}`;
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`stats_${cacheKey}`]) {
        return apiCache.pendingPromises[`stats_${cacheKey}`];
      }
      
      // Verificar si tenemos caché válida
      if (!apiCache.lastFetch.stats) {
        apiCache.lastFetch.stats = {};
      }
      
      if (isCacheValid('stats', cacheKey)) {
        const cachedStats = sessionStorage.getItem(`stats_${cacheKey}`);
        if (cachedStats) {
          try {
            return JSON.parse(cachedStats);
          } catch (e) {
            console.error('Error al parsear estadísticas en caché:', e);
          }
        }
      }
      
      // Verificar si podemos hacer una nueva solicitud (throttling)
      if (!canMakeRequest('stats', cacheKey)) {
        // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
        const cachedStats = sessionStorage.getItem(`stats_${cacheKey}`);
        if (cachedStats) {
          try {
            return JSON.parse(cachedStats);
          } catch (e) {
            console.error('Error al parsear estadísticas en caché:', e);
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const statsPromise = (async () => {
        try {
          const response = await apiClient.get(`/fit-stats?period=${period}&date=${date}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`stats_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.stats[cacheKey] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener estadísticas:', error);
          
          // Intentar usar caché si hay error
          const cachedStats = sessionStorage.getItem(`stats_${cacheKey}`);
          if (cachedStats) {
            try {
              return JSON.parse(cachedStats);
            } catch (e) {
              console.error('Error al parsear estadísticas en caché:', e);
            }
          }
          
          return { success: false, data: null, error: error.message };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`stats_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`stats_${cacheKey}`] = statsPromise;
      
      return statsPromise;
    } catch (error) {
      console.error('Error inesperado en getStats:', error);
      return { success: false, data: null, error: error.message };
    }
  },

  /**
   * Obtiene estadísticas diarias por fecha
   * @param {string} date Fecha en formato YYYY-MM-DD
   * @returns {Promise<Object>} Respuesta con las estadísticas diarias
   */
  getDailyStats: async (date = new Date().toISOString().split('T')[0]) => {
    try {
      const now = Date.now();
      const cacheKey = `daily_${date}`;
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[cacheKey]) {
        return apiCache.pendingPromises[cacheKey];
      }
      
      // Verificar si tenemos caché válida
      if (!apiCache.lastFetch.dailyStats) {
        apiCache.lastFetch.dailyStats = {};
      }
      
      if (isCacheValid('dailyStats', date)) {
        const cachedStats = sessionStorage.getItem(cacheKey);
        if (cachedStats) {
          try {
            return JSON.parse(cachedStats);
          } catch (e) {
            console.error('Error al parsear estadísticas diarias en caché:', e);
          }
        }
      }
      
      // Verificar si podemos hacer una nueva solicitud (throttling)
      if (!canMakeRequest('dailyStats', date)) {
        // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
        const cachedStats = sessionStorage.getItem(cacheKey);
        if (cachedStats) {
          try {
            return JSON.parse(cachedStats);
          } catch (e) {
            console.error('Error al parsear estadísticas diarias en caché:', e);
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const statsPromise = (async () => {
        try {
          const response = await apiClient.get(`/fit-daily-stats?date=${date}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.dailyStats[date] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener estadísticas diarias:', error);
          
          // Intentar usar caché si hay error
          const cachedStats = sessionStorage.getItem(cacheKey);
          if (cachedStats) {
            try {
              return JSON.parse(cachedStats);
            } catch (e) {
              console.error('Error al parsear estadísticas diarias en caché:', e);
            }
          }
          
          return { 
            success: false, 
            data: { 
              caloriesBurned: 0, 
              calories: 0, 
              protein: 0, 
              carbs: 0, 
              fats: 0, 
              water: 0, 
              steps: 0 
            }, 
            error: error.message 
          };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[cacheKey];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[cacheKey] = statsPromise;
      
      return statsPromise;
    } catch (error) {
      console.error('Error inesperado en getDailyStats:', error);
      return { 
        success: false, 
        data: { 
          caloriesBurned: 0, 
          calories: 0, 
          protein: 0, 
          carbs: 0, 
          fats: 0, 
          water: 0, 
          steps: 0 
        }, 
        error: error.message 
      };
    }
  },

  /**
   * Actualiza las estadísticas diarias
   * @param {string} date Fecha en formato YYYY-MM-DD
   * @param {Object} statsData Datos de estadísticas a actualizar
   * @returns {Promise<Object>} Respuesta con las estadísticas actualizadas
   */
  updateDailyStats: async (date, statsData) => {
    try {
      const response = await apiClient.post('/fit-daily-stats/update', { 
        date, 
        statsData 
      });
      
      // Invalidar caché para esta fecha
      const cacheKey = `daily_${date}`;
      if (apiCache.lastFetch.dailyStats) {
        delete apiCache.lastFetch.dailyStats[date];
      }
      sessionStorage.removeItem(cacheKey);
      
      return response.data;
    } catch (error) {
      console.error('Error al actualizar estadísticas diarias:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Actualiza específicamente las calorías quemadas para un día
   * @param {string} date Fecha en formato YYYY-MM-DD
   * @param {number} caloriesBurned Calorías quemadas
   * @returns {Promise<Object>} Respuesta con el resultado
   */
  updateDayCaloriesBurned: async (date, caloriesBurned) => {
    try {
      const response = await apiClient.post('/fit-exercises/update-calories', {
        date,
        caloriesBurned
      });
      
      // Invalidar caché para esta fecha
      const cacheKey = `daily_${date}`;
      if (apiCache.lastFetch.dailyStats) {
        delete apiCache.lastFetch.dailyStats[date];
      }
      sessionStorage.removeItem(cacheKey);
      
      // También invalidar la caché de ejercicios para esta fecha
      if (apiCache.lastFetch.exercises) {
        delete apiCache.lastFetch.exercises[date];
      }
      sessionStorage.removeItem(`exercises_${date}`);
      
      return response.data;
    } catch (error) {
      console.error('Error al actualizar calorías quemadas del día:', error);
      return { success: false, error: error.message };
    }
  },
 
  /**
   * Obtiene alimentos favoritos con control de caché y frecuencia
   * @returns {Promise<Object>} Respuesta con los alimentos favoritos
   */
  getFavoriteFoods: async () => {
    try {
      const now = Date.now();
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises.favoriteFoods) {
        return apiCache.pendingPromises.favoriteFoods;
      }
      
      // Verificar si tenemos caché válida
      if (isCacheValid('favorites')) {
        const cachedFavorites = sessionStorage.getItem('favorite_foods');
        if (cachedFavorites) {
          try {
            return JSON.parse(cachedFavorites);
          } catch (e) {
            console.error('Error al parsear favoritos en caché:', e);
          }
        }
      }
      
      // Verificar si podemos hacer una nueva solicitud (throttling)
      if (!canMakeRequest('favorites')) {
        // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
        const cachedFavorites = sessionStorage.getItem('favorite_foods');
        if (cachedFavorites) {
          try {
            return JSON.parse(cachedFavorites);
          } catch (e) {
            console.error('Error al parsear favoritos en caché:', e);
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const favoritesPromise = (async () => {
        try {
          const response = await apiClient.get('/fit-foods/favorites');
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem('favorite_foods', JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.favorites = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener alimentos favoritos:', error);
          
          // Intentar usar caché si hay error
          const cachedFavorites = sessionStorage.getItem('favorite_foods');
          if (cachedFavorites) {
            try {
              return JSON.parse(cachedFavorites);
            } catch (e) {
              console.error('Error al parsear favoritos en caché:', e);
            }
          }
          
          return { success: false, data: [], error: error.message };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises.favoriteFoods;
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises.favoriteFoods = favoritesPromise;
      
      return favoritesPromise;
    } catch (error) {
      console.error('Error inesperado en getFavoriteFoods:', error);
      return { success: false, data: [], error: error.message };
    }
  },
 
  // Añadir alimento a favoritos
  addFavoriteFood: async (foodData) => {
    try {
      const response = await apiClient.post('/fit-foods/favorites', foodData);
      
      // Invalidar caché de favoritos
      delete apiCache.lastFetch.favorites;
      sessionStorage.removeItem('favorite_foods');
      
      return response.data;
    } catch (error) {
      console.error('Error al añadir alimento a favoritos:', error);
      return { success: false, data: null, error: error.message };
    }
  },
 
  // Eliminar alimento de favoritos
  removeFavoriteFood: async (foodId) => {
    try {
      const response = await apiClient.delete(`/fit-foods/favorites/${foodId}`);
      
      // Invalidar caché de favoritos
      delete apiCache.lastFetch.favorites;
      sessionStorage.removeItem('favorite_foods');
      
      return response.data;
    } catch (error) {
      console.error('Error al eliminar alimento de favoritos:', error);
      return { success: false, error: error.message };
    }
  },
 
  /**
   * Obtiene historial de alimentos con control de caché y frecuencia
   * @returns {Promise<Object>} Respuesta con el historial de alimentos
   */
  getFoodHistory: async () => {
    try {
      const now = Date.now();
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises.foodHistory) {
        return apiCache.pendingPromises.foodHistory;
      }
      
      // Verificar si tenemos caché válida
      if (isCacheValid('history')) {
        const cachedHistory = sessionStorage.getItem('food_history');
        if (cachedHistory) {
          try {
            return JSON.parse(cachedHistory);
          } catch (e) {
            console.error('Error al parsear historial en caché:', e);
          }
        }
      }
      
      // Verificar si podemos hacer una nueva solicitud (throttling)
      if (!canMakeRequest('history')) {
        // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
        const cachedHistory = sessionStorage.getItem('food_history');
        if (cachedHistory) {
          try {
            return JSON.parse(cachedHistory);
          } catch (e) {
            console.error('Error al parsear historial en caché:', e);
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const historyPromise = (async () => {
        try {
          const response = await apiClient.get('/fit-foods/history');
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem('food_history', JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.history = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener historial de alimentos:', error);
          
          // Intentar usar caché si hay error
          const cachedHistory = sessionStorage.getItem('food_history');
          if (cachedHistory) {
            try {
              return JSON.parse(cachedHistory);
            } catch (e) {
              console.error('Error al parsear historial en caché:', e);
            }
          }
          
          return { success: false, data: [], error: error.message };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises.foodHistory;
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises.foodHistory = historyPromise;
      
      return historyPromise;
    } catch (error) {
      console.error('Error inesperado en getFoodHistory:', error);
      return { success: false, data: [], error: error.message };
    }
  },
 
  // Crear alimento personalizado
  createCustomFood: async (foodData) => {
    try {
      const response = await apiClient.post('/fit-foods/custom', foodData);
      
      // Invalidar cachés relacionadas
      delete apiCache.lastFetch.history;
      delete apiCache.lastFetch.userFoods;
      sessionStorage.removeItem('food_history');
      sessionStorage.removeItem('user_foods');
      
      return response.data;
    } catch (error) {
      console.error('Error al crear alimento personalizado:', error);
      return { success: false, data: null, error: error.message };
    }
  },
 
  /**
   * Obtiene alimentos creados por el usuario con control de caché y frecuencia
   * @returns {Promise<Object>} Respuesta con los alimentos del usuario
   */
  getUserCreatedFoods: async () => {
    try {
      const now = Date.now();
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises.userFoods) {
        return apiCache.pendingPromises.userFoods;
      }
      
      // Verificar si tenemos caché válida
      if (isCacheValid('userFoods')) {
        const cachedUserFoods = sessionStorage.getItem('user_foods');
        if (cachedUserFoods) {
          try {
            return JSON.parse(cachedUserFoods);
          } catch (e) {
            console.error('Error al parsear alimentos del usuario en caché:', e);
          }
        }
      }
      
      // Verificar si podemos hacer una nueva solicitud (throttling)
      if (!canMakeRequest('userFoods')) {
        // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
        const cachedUserFoods = sessionStorage.getItem('user_foods');
        if (cachedUserFoods) {
          try {
            return JSON.parse(cachedUserFoods);
          } catch (e) {
            console.error('Error al parsear alimentos del usuario en caché:', e);
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const userFoodsPromise = (async () => {
        try {
          const response = await apiClient.get('/fit-foods/custom');
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem('user_foods', JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.userFoods = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener alimentos creados:', error);
          
          // Intentar usar caché si hay error
          const cachedUserFoods = sessionStorage.getItem('user_foods');
          if (cachedUserFoods) {
            try {
              return JSON.parse(cachedUserFoods);
            } catch (e) {
              console.error('Error al parsear alimentos del usuario en caché:', e);
            }
          }
          
          return { success: false, data: [], error: error.message };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises.userFoods;
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises.userFoods = userFoodsPromise;
      
      return userFoodsPromise;
    } catch (error) {
      console.error('Error inesperado en getUserCreatedFoods:', error);
      return { success: false, data: [], error: error.message };
    }
  },
 
  /**
   * Obtiene ejercicios del día con control de caché y frecuencia
   * @param {string} date Fecha en formato YYYY-MM-DD
   * @returns {Promise<Object>} Respuesta con los ejercicios
   */
  getExercises: async (date = new Date().toISOString().split('T')[0]) => {
    try {
      const now = Date.now();
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`exercises_${date}`]) {
        return apiCache.pendingPromises[`exercises_${date}`];
      }
      
      // Verificar si tenemos caché válida
      if (!apiCache.lastFetch.exercises) {
        apiCache.lastFetch.exercises = {};
      }
      
      if (isCacheValid('exercises', date)) {
        const cachedExercises = sessionStorage.getItem(`exercises_${date}`);
        if (cachedExercises) {
          try {
            return JSON.parse(cachedExercises);
          } catch (e) {
            console.error('Error al parsear ejercicios en caché:', e);
          }
        }
      }
      
      // Verificar si podemos hacer una nueva solicitud (throttling)
      if (!canMakeRequest('exercises', date)) {
        // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
        const cachedExercises = sessionStorage.getItem(`exercises_${date}`);
        if (cachedExercises) {
          try {
            return JSON.parse(cachedExercises);
          } catch (e) {
            console.error('Error al parsear ejercicios en caché:', e);
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const exercisesPromise = (async () => {
        try {
          const response = await apiClient.get(`/fit-exercises?date=${date}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`exercises_${date}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            apiCache.lastFetch.exercises[date] = now;
          }
          
          return response.data;
        } catch (error) {
          console.error('Error al obtener ejercicios:', error);
          
          // Intentar usar caché si hay error
          const cachedExercises = sessionStorage.getItem(`exercises_${date}`);
          if (cachedExercises) {
            try {
              return JSON.parse(cachedExercises);
            } catch (e) {
              console.error('Error al parsear ejercicios en caché:', e);
            }
          }
          
          return { success: false, data: [], error: error.message };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`exercises_${date}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`exercises_${date}`] = exercisesPromise;
      
      return exercisesPromise;
    } catch (error) {
      console.error('Error inesperado en getExercises:', error);
      return { success: false, data: [], error: error.message };
    }
  },

  // Actualizar una comida existente
  updateMeal: async (mealId, mealData) => {
    try {
      const response = await apiClient.put(`/fit-meals/${mealId}`, mealData);
      
      // Invalidar caché de comidas y estadísticas para ese día
      const date = mealData.date || new Date().toISOString().split('T')[0];
      
      // Limpiar caché de comidas
      if (!apiCache.lastFetch.meals) {
        apiCache.lastFetch.meals = {};
      }
      
      // Invalidar todas las cachés de comidas relacionadas con la fecha
      Object.keys(apiCache.lastFetch.meals).forEach(key => {
        if (key.includes(date)) {
          delete apiCache.lastFetch.meals[key];
          sessionStorage.removeItem(`meals_${key}`);
        }
      });
      
      // Limpiar caché de estadísticas
      if (!apiCache.lastFetch.stats) {
        apiCache.lastFetch.stats = {};
      }
      
      // Invalidar todas las cachés de estadísticas relacionadas con la fecha
      Object.keys(apiCache.lastFetch.stats).forEach(key => {
        if (key.includes(date)) {
          delete apiCache.lastFetch.stats[key];
          sessionStorage.removeItem(`stats_${key}`);
        }
      });

      // Invalidar caché de estadísticas diarias
      if (apiCache.lastFetch.dailyStats && apiCache.lastFetch.dailyStats[date]) {
        delete apiCache.lastFetch.dailyStats[date];
        sessionStorage.removeItem(`daily_${date}`);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al actualizar comida:', error);
      return { success: false, data: null, error: error.message };
    }
  },
  
  // Eliminar una comida
  deleteMeal: async (mealId, date = new Date().toISOString().split('T')[0]) => {
    try {
      
      const response = await apiClient.delete(`/fit-meals/${mealId}`);
      
      // Invalidar caché de comidas y estadísticas
      
      // Limpiar caché de comidas
      if (!apiCache.lastFetch.meals) {
        apiCache.lastFetch.meals = {};
      }
      
      // Invalidar todas las cachés de comidas relacionadas con la fecha
      Object.keys(apiCache.lastFetch.meals).forEach(key => {
        if (key.includes(date)) {
          delete apiCache.lastFetch.meals[key];
          sessionStorage.removeItem(`meals_${key}`);
        }
      });
      
      // Limpiar caché de estadísticas
      if (!apiCache.lastFetch.stats) {
        apiCache.lastFetch.stats = {};
      }
      
      // Invalidar todas las cachés de estadísticas relacionadas con la fecha
      Object.keys(apiCache.lastFetch.stats).forEach(key => {
        if (key.includes(date)) {
          delete apiCache.lastFetch.stats[key];
          sessionStorage.removeItem(`stats_${key}`);
        }
      });

      // Invalidar caché de estadísticas diarias
      if (apiCache.lastFetch.dailyStats && apiCache.lastFetch.dailyStats[date]) {
        delete apiCache.lastFetch.dailyStats[date];
        sessionStorage.removeItem(`daily_${date}`);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al eliminar comida:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Obtiene check-ins con control de caché y frecuencia
   * @returns {Promise<Object>} Respuesta con los check-ins
   */
  getCheckIns: async () => {
    try {
      const now = Date.now();
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises.checkIns) {
        return apiCache.pendingPromises.checkIns;
      }
      
      // Verificar si tenemos caché válida
      if (apiCache.checkIns && isCacheValid('checkIns')) {
        return { success: true, data: apiCache.checkIns };
      }
      
      // Verificar si podemos hacer una nueva solicitud (throttling)
      if (!canMakeRequest('checkIns') && apiCache.checkIns) {
        return { success: true, data: apiCache.checkIns, fromCache: true };
      }
      
      // Crear una promesa para la solicitud actual
      const checkInsPromise = (async () => {
        try {
          const response = await apiClient.get('/fit-checkins');
          
          // Actualizar caché
          if (response.data.success && Array.isArray(response.data.data)) {
            apiCache.checkIns = response.data.data;
            apiCache.lastFetch.checkIns = now;
          }
     
          return response.data;
        } catch (error) {
          console.error('Error al obtener check-ins:', error);
          
          // Si hay datos en caché, devolverlos en caso de error
          if (apiCache.checkIns) {
            return { success: true, data: apiCache.checkIns, fromCache: true };
          }
          
          return { success: false, data: [], error: error.message };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises.checkIns;
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises.checkIns = checkInsPromise;
      
      return checkInsPromise;
    } catch (error) {
      console.error('Error inesperado en getCheckIns:', error);
      return { success: false, data: [], error: error.message };
    }
  },
 
  // Añadir check-in
  addCheckIn: async (checkInData) => {
    try {
      const response = await apiClient.post('/fit-checkins', checkInData);
      
      // Invalidar caché de check-ins y objetivos
      apiCache.checkIns = null;
      apiCache.lastFetch.checkIns = null;
      apiCache.goal = null;
      apiCache.lastFetch.goal = null;
      
      // Invalidar caché de estadísticas de progreso
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('progress_')) {
          sessionStorage.removeItem(key);
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error al añadir check-in:', error);
      return { success: false, data: null, error: error.message };
    }
  },
 
  // Actualizar un check-in existente
  updateCheckIn: async (checkInId, checkInData) => {
    try {
      const response = await apiClient.put(`/fit-checkins/${checkInId}`, checkInData);
      
      // Invalidar caché de check-ins y objetivos
      apiCache.checkIns = null;
      apiCache.lastFetch.checkIns = null;
      apiCache.goal = null;
      apiCache.lastFetch.goal = null;
      
      // Invalidar caché de estadísticas de progreso
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('progress_')) {
          sessionStorage.removeItem(key);
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error al actualizar check-in:', error);
      return { success: false, data: null, error: error.message };
    }
  },
  
  // Eliminar check-in
  deleteCheckIn: async (checkInId) => {
    try {
      const response = await apiClient.delete(`/fit-checkins/${checkInId}`);
      
      // Invalidar caché de check-ins y objetivos
      apiCache.checkIns = null;
      apiCache.lastFetch.checkIns = null;
      apiCache.goal = null;
      apiCache.lastFetch.goal = null;
      
      // Invalidar caché de estadísticas de progreso
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('progress_')) {
          sessionStorage.removeItem(key);
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error al eliminar check-in:', error);
      return { success: false, error: error.message };
    }
  },
 
  /**
   * Obtiene estadísticas de progreso con control de caché y frecuencia
   * @param {string} metric Métrica (weight, bodyFat, etc.)
   * @param {string} period Período (week, month, etc.)
   * @returns {Promise<Object>} Respuesta con las estadísticas
   */
  getProgressStats: async (metric = 'weight', period = 'week') => {
    try {
      const now = Date.now();
      const cacheKey = `${metric}_${period}`;
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[`progress_${cacheKey}`]) {
        return apiCache.pendingPromises[`progress_${cacheKey}`];
      }
      
      // Verificar si tenemos caché válida
      if (isCacheValid('stats', `progress_${cacheKey}`)) {
        const cachedStats = sessionStorage.getItem(`progress_${cacheKey}`);
        if (cachedStats) {
          try {
            return JSON.parse(cachedStats);
          } catch (e) {
            console.error('Error al parsear estadísticas de progreso en caché:', e);
          }
        }
      }
      
      // Verificar si podemos hacer una nueva solicitud (throttling)
      if (!canMakeRequest('stats', `progress_${cacheKey}`)) {
        // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
        const cachedStats = sessionStorage.getItem(`progress_${cacheKey}`);
        if (cachedStats) {
          try {
            return JSON.parse(cachedStats);
          } catch (e) {
            console.error('Error al parsear estadísticas de progreso en caché:', e);
          }
        }
      }
      
      // Crear una promesa para la solicitud actual
      const progressPromise = (async () => {
        try {
          const response = await apiClient.get(`/fit-checkins/stats?metric=${metric}&period=${period}`);
          
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(`progress_${cacheKey}`, JSON.stringify(response.data));
            
            // Actualizar tiempo de última obtención
            if (!apiCache.lastFetch.stats) {
              apiCache.lastFetch.stats = {};
            }
            apiCache.lastFetch.stats[`progress_${cacheKey}`] = now;
          }
     
          return response.data;
        } catch (error) {
          console.error('Error al obtener estadísticas de progreso:', error);
          
          // Intentar usar caché si hay error
          const cachedStats = sessionStorage.getItem(`progress_${cacheKey}`);
          if (cachedStats) {
            try {
              return JSON.parse(cachedStats);
            } catch (e) {
              console.error('Error al parsear estadísticas de progreso en caché:', e);
            }
          }
          
          return { success: false, data: [], error: error.message };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[`progress_${cacheKey}`];
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[`progress_${cacheKey}`] = progressPromise;
      
      return progressPromise;
    } catch (error) {
      console.error('Error inesperado en getProgressStats:', error);
      return { success: false, data: [], error: error.message };
    }
  },
 
  /**
   * Obtiene el objetivo del usuario con control de caché y frecuencia
   * @returns {Promise<Object>} Respuesta con el objetivo
   */
  getGoal: async () => {
    try {
      const now = Date.now();
      
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises.goal) {
        return apiCache.pendingPromises.goal;
      }
      
      // Verificar si tenemos caché válida
      if (apiCache.goal && isCacheValid('goal')) {
        return { success: true, data: apiCache.goal };
      }
      
      // Verificar si podemos hacer una nueva solicitud (throttling)
      if (!canMakeRequest('goal') && apiCache.goal) {
        return { success: true, data: apiCache.goal, fromCache: true };
      }
      
      // Crear una promesa para la solicitud actual
      const goalPromise = (async () => {
        try {
          const response = await apiClient.get('/fit-goals');
          
          // Actualizar caché
          if (response.data.success && response.data.data) {
            apiCache.goal = response.data.data;
            apiCache.lastFetch.goal = now;
          }
     
          return response.data;
        } catch (error) {
          console.error('Error al obtener objetivo:', error);
          
          // Si hay datos en caché, devolverlos en caso de error
          if (apiCache.goal) {
            return { success: true, data: apiCache.goal, fromCache: true };
          }
          
          return { success: false, data: null, error: error.message };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises.goal;
        }
      })();
      
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises.goal = goalPromise;
      
      return goalPromise;
    } catch (error) {
      console.error('Error inesperado en getGoal:', error);
      return { success: false, data: null, error: error.message };
    }
  },
 
  // Actualizar objetivo
  updateGoal: async (goalData) => {
    try {
      const response = await apiClient.put('/fit-goals', goalData);
  
      // Invalidar caché de objetivos y perfil
      apiCache.goal = null;
      apiCache.lastFetch.goal = null;
      apiCache.fitProfile = null;
      apiCache.lastFetch.fitProfile = null;
  
      // Eliminar también del localStorage
      localStorage.removeItem('agtfit_profile');
  
      return response.data;
    } catch (error) {
      console.error('Error al actualizar objetivo:', error);
      return { success: false, data: null, error: error.message };
    }
  },
  
  // Método para limpiar caché (útil para logout o debugging)
  clearCache: () => {
    // Limpiar caché en memoria
    apiCache.fitProfile = null;
    apiCache.checkIns = null;
    apiCache.goal = null;
    
    // Limpiar todos los tiempos de caché
    apiCache.lastFetch = {
      fitProfile: null,
      checkIns: null,
      goal: null,
      stats: {},
      dailyStats: {},
      meals: {},
      exercises: {}
    };
    
    // Limpiar intentos de solicitud
    apiCache.requestAttempts = {
      fitProfile: null,
      checkIns: null,
      goal: null
    };
    
    // Limpiar promesas pendientes
    apiCache.pendingPromises = {};
    
    // Limpiar contadores de errores
    apiCache.errorCount = {};
    
    // Limpiar localStorage relacionado con AGT Fit
    localStorage.removeItem('agtfit_profile');
    localStorage.removeItem('agtfit_goal');
    localStorage.removeItem('agtfit_approach');
    localStorage.removeItem('agtfit_calories');
    localStorage.removeItem('agtfit_macros');
    localStorage.removeItem('agtfit_goalWeight');
    
    // Limpiar sessionStorage relacionado con AGT Fit
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key.startsWith('meals_') || 
          key.startsWith('stats_') || 
          key.startsWith('exercises_') || 
          key.startsWith('progress_') ||
          key.startsWith('daily_') ||
          key === 'favorite_foods' ||
          key === 'food_history' ||
          key === 'user_foods') {
        keys.push(key);
      }
    }
    
    keys.forEach(key => sessionStorage.removeItem(key));
    
    return { success: true };
  },
  
  // Método para obtener el estado actual de la caché (útil para debugging)
  getCacheStatus: () => {
    return {
      fitProfile: !!apiCache.fitProfile,
      checkIns: !!apiCache.checkIns,
      goal: !!apiCache.goal,
      lastFetch: { ...apiCache.lastFetch },
      pendingPromises: Object.keys(apiCache.pendingPromises),
      errorCount: { ...apiCache.errorCount }
    };
  },

  // Buscar en el catálogo de ejercicios
  searchExerciseCatalog: async (query = '', muscle = '', equipment = '', language = 'en') => {
    try {
      let url = '/fit-exercise-catalog/search?';
  
      if (query) url += `query=${encodeURIComponent(query)}&`;
      if (muscle) url += `muscle=${encodeURIComponent(muscle)}&`;
      if (equipment) url += `equipment=${encodeURIComponent(equipment)}&`;
      if (language) url += `language=${encodeURIComponent(language)}&`;
  
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error al buscar ejercicios:', error);
      return { success: false, data: [], error: error.message };
    }
  },
  
  // Obtener todos los ejercicios del catálogo
  getAllExerciseCatalog: async () => {
    try {
      const response = await apiClient.get('/fit-exercise-catalog');
      return response.data;
    } catch (error) {
      console.error('Error al obtener catálogo de ejercicios:', error);
      return { success: false, data: [], error: error.message };
    }
  },
  
  // Obtener un ejercicio del catálogo por ID
  getExerciseFromCatalog: async (exerciseId) => {
    try {
      const response = await apiClient.get(`/fit-exercise-catalog/${exerciseId}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener ejercicio del catálogo:', error);
      return { success: false, data: null, error: error.message };
    }
  },

  logExercise: async (exerciseData) => {
    try {
      const response = await apiClient.post('/fit-exercises', exerciseData);
  
      // Invalidar caché de ejercicios para ese día
      const date = exerciseData.date || new Date().toISOString().split('T')[0];
  
      if (!apiCache.lastFetch.exercises) {
        apiCache.lastFetch.exercises = {};
      }
  
      delete apiCache.lastFetch.exercises[date];
      sessionStorage.removeItem(`exercises_${date}`);

      // Invalidar caché de estadísticas diarias
      if (apiCache.lastFetch.dailyStats && apiCache.lastFetch.dailyStats[date]) {
        delete apiCache.lastFetch.dailyStats[date];
        sessionStorage.removeItem(`daily_${date}`);
      }
  
      return response.data;
    } catch (error) {
      console.error('Error al registrar ejercicio:', error);
      return { success: false, data: null, error: error.message };
    }
  },

  getWorkoutStats: async (startDate, endDate) => {
    try {
      const response = await apiClient.get('/fit-exercises/stats', {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching workout stats:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to load workout statistics'
      };
    }
  },

  deleteExercise: async (exerciseId) => {
    try {
      const response = await apiClient.delete(`/fit-exercises/${exerciseId}`);
  
      // Invalidar caché de ejercicios
      if (!apiCache.lastFetch.exercises) {
        apiCache.lastFetch.exercises = {};
      }
  
      // Invalidar todas las cachés de ejercicios
      Object.keys(apiCache.lastFetch.exercises).forEach(key => {
        delete apiCache.lastFetch.exercises[key];
        sessionStorage.removeItem(`exercises_${key}`);
      });

      // Invalidar todas las cachés de estadísticas diarias
      if (apiCache.lastFetch.dailyStats) {
        Object.keys(apiCache.lastFetch.dailyStats).forEach(date => {
          delete apiCache.lastFetch.dailyStats[date];
          sessionStorage.removeItem(`daily_${date}`);
        });
      }
  
      return response.data;
    } catch (error) {
      console.error('Error al eliminar ejercicio:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener todas las rutinas
  getRoutines: async () => {
    try {
      const now = Date.now();
      const cacheKey = 'routines';
  
      // Si hay una promesa pendiente para esta solicitud, devolver esa promesa
      if (apiCache.pendingPromises[cacheKey]) {
        return apiCache.pendingPromises[cacheKey];
      }
  
      // Verificar si tenemos caché válida
      if (isCacheValid('routines')) {
        const cachedRoutines = sessionStorage.getItem(cacheKey);
        if (cachedRoutines) {
          try {
            return JSON.parse(cachedRoutines);
          } catch (e) {
            console.error('Error al parsear rutinas en caché:', e);
          }
        }
      }
  
      // Verificar si podemos hacer una nueva solicitud (throttling)
      if (!canMakeRequest('routines')) {
        // Si no podemos hacer una solicitud, usar los datos en caché (incluso si están expirados)
        const cachedRoutines = sessionStorage.getItem(cacheKey);
        if (cachedRoutines) {
          try {
            return JSON.parse(cachedRoutines);
          } catch (e) {
            console.error('Error al parsear rutinas en caché:', e);
          }
        }
      }
  
      // Crear una promesa para la solicitud actual
      const routinesPromise = (async () => {
        try {
          const response = await apiClient.get('/fit-routines');
  
          // Actualizar caché
          if (response.data.success) {
            // Guardar en sessionStorage
            sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
  
            // Actualizar tiempo de última obtención
            if (!apiCache.lastFetch.routines) {
              apiCache.lastFetch.routines = now;
            }
          }
  
          return response.data;
        } catch (error) {
          console.error('Error al obtener rutinas:', error);
  
          // Intentar usar caché si hay error
          const cachedRoutines = sessionStorage.getItem(cacheKey);
          if (cachedRoutines) {
            try {
              return JSON.parse(cachedRoutines);
            } catch (e) {
              console.error('Error al parsear rutinas en caché:', e);
            }
          }
  
          return { success: false, data: [], error: error.message };
        } finally {
          // Eliminar la promesa pendiente cuando se complete
          delete apiCache.pendingPromises[cacheKey];
        }
      })();
  
      // Almacenar la promesa para posibles solicitudes simultáneas
      apiCache.pendingPromises[cacheKey] = routinesPromise;
  
      return routinesPromise;
    } catch (error) {
      console.error('Error inesperado en getRoutines:', error);
      return { success: false, data: [], error: error.message };
    }
  },
  
  // Crear una nueva rutina
  createRoutine: async (routineData) => {
    try {
      const response = await apiClient.post('/fit-routines', routineData);
  
      // Invalidar caché de rutinas
      delete apiCache.lastFetch.routines;
      sessionStorage.removeItem('routines');
  
      return response.data;
    } catch (error) {
      console.error('Error al crear rutina:', error);
      return { success: false, data: null, error: error.message };
    }
  },
  
  // Obtener una rutina específica
  getRoutine: async (routineId) => {
    try {
      const response = await apiClient.get(`/fit-routines/${routineId}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener rutina:', error);
      return { success: false, data: null, error: error.message };
    }
  },
  
  // Actualizar una rutina
  updateRoutine: async (routineId, routineData) => {
    try {
      const response = await apiClient.put(`/fit-routines/${routineId}`, routineData);
  
      // Invalidar caché de rutinas
      delete apiCache.lastFetch.routines;
      sessionStorage.removeItem('routines');
  
      return response.data;
    } catch (error) {
      console.error('Error al actualizar rutina:', error);
      return { success: false, data: null, error: error.message };
    }
  },
  
  // Eliminar una rutina
  deleteRoutine: async (routineId) => {
    try {
      const response = await apiClient.delete(`/fit-routines/${routineId}`);
  
      // Invalidar caché de rutinas
      delete apiCache.lastFetch.routines;
      sessionStorage.removeItem('routines');
  
      return response.data;
    } catch (error) {
      console.error('Error al eliminar rutina:', error);
      return { success: false, error: error.message };
    }
  },

  // Registrar un entrenamiento completo
  logWorkout: async function(workoutData) {
    try {
      const response = await apiClient.post('/fit-workouts', workoutData);
  
      // Limpiar caché de ejercicios para ese día
      const date = workoutData.date || new Date().toISOString().split('T')[0];
  
      if (!apiCache.lastFetch.exercises) {
        apiCache.lastFetch.exercises = {};
      }
  
      delete apiCache.lastFetch.exercises[date];
      sessionStorage.removeItem(`exercises_${date}`);

      // Invalidar caché de estadísticas diarias
      if (apiCache.lastFetch.dailyStats && apiCache.lastFetch.dailyStats[date]) {
        delete apiCache.lastFetch.dailyStats[date];
        sessionStorage.removeItem(`daily_${date}`);
      }
  
      return response.data;
    } catch (error) {
      console.error('Error logging workout:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to log workout' 
      };
    }
  },
  
  // Obtener calorías quemadas para un ejercicio específico
  getExerciseCalories: async function(exerciseId) {
    try {
      const response = await apiClient.get(`/fit-exercise-catalog/${exerciseId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting exercise calories:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to get exercise calories' 
      };
    }
  },
  
  // Reemplazar la función resetBurnedCalories con una versión que use estadísticas diarias
  resetBurnedCalories: async function(caloriesBurned = 0) {
    try {
      // Obtener la fecha actual
      const date = new Date().toISOString().split('T')[0];
      
      // Usar el nuevo método para actualizar las calorías quemadas del día
      return await this.updateDayCaloriesBurned(date, caloriesBurned);
    } catch (error) {
      console.error('Error resetting burned calories:', error);
      return { success: false, error: error.message };
    }
  },

  // Buscar historial de ejercicios por nombre
  searchExerciseHistory: async function(exerciseName) {
    try {
      const response = await apiClient.get(`/fit-exercises/history?name=${encodeURIComponent(exerciseName)}`);
      return response.data;
    } catch (error) {
      console.error('Error searching exercise history:', error);
      return { 
        success: false, 
        data: [],
        error: error.response?.data?.message || 'Failed to get exercise history' 
      };
    }
  },

  /**
   * Invalida selectivamente cachés para forzar recarga de datos
   * @param {string} date Fecha en formato YYYY-MM-DD para invalidar datos específicos de ese día
   * @param {Array<string>} types Tipos de caché a invalidar ('profile', 'meals', 'dailyStats', 'exercises', etc.)
   */
  invalidateCache: (date, types = ['profile', 'meals', 'dailyStats', 'exercises']) => {
    const formattedDate = date || new Date().toISOString().split('T')[0];
  
    if (types.includes('profile')) {
      apiCache.fitProfile = null;
      apiCache.lastFetch.fitProfile = null;
      localStorage.removeItem('agtfit_profile');
    }
  
    if (types.includes('meals') && apiCache.lastFetch.meals) {
      Object.keys(apiCache.lastFetch.meals).forEach(key => {
        if (key.includes(formattedDate)) {
          delete apiCache.lastFetch.meals[key];
          sessionStorage.removeItem(`meals_${key}`);
        }
      });
    }
  
    if (types.includes('dailyStats') && apiCache.lastFetch.dailyStats) {
      delete apiCache.lastFetch.dailyStats[formattedDate];
      sessionStorage.removeItem(`daily_${formattedDate}`);
    }
  
    if (types.includes('exercises') && apiCache.lastFetch.exercises) {
      delete apiCache.lastFetch.exercises[formattedDate];
      sessionStorage.removeItem(`exercises_${formattedDate}`);
    }
  
    if (types.includes('stats') && apiCache.lastFetch.stats) {
      Object.keys(apiCache.lastFetch.stats).forEach(key => {
        if (key.includes(formattedDate)) {
          delete apiCache.lastFetch.stats[key];
          sessionStorage.removeItem(`stats_${key}`);
        }
      });
    }
  
    return { success: true };
  }
};

// Conexión con datos de salud
agtfitService.connectHealthData = async () => {
  try {
    // Verificar si hay una promesa pendiente para esta solicitud
    if (apiCache.pendingPromises.healthData) {
      return apiCache.pendingPromises.healthData;
    }
 
    // Crear una promesa para la solicitud actual
    const healthDataPromise = (async () => {
      try {
        const response = await apiClient.get('/fit-health');
 
        // Almacenar los datos en sessionStorage para acceso rápido
        if (response.data.success) {
          sessionStorage.setItem('agtfit_health_data', JSON.stringify(response.data));
        }
 
        return response.data;
      } catch (error) {
        console.error('Error connecting to health data:', error);
 
        // Si es 404, no es un error técnico, solo significa que no hay perfil de salud
        if (error.response && error.response.status === 404) {
          // Eliminar el estado de conexión si el perfil ya no existe
          localStorage.removeItem('agtfit_health_connected');
 
          return { 
            success: false, 
            message: error.response.data.message || "No health profile found",
            status: 404
          };
        }
 
        // Intentar usar datos de caché si hay un error
        const cachedHealthData = sessionStorage.getItem('agtfit_health_data');
        if (cachedHealthData) {
          try {
            return JSON.parse(cachedHealthData);
          } catch (e) {
            console.error('Error parsing cached health data:', e);
          }
        }
 
        return { 
          success: false, 
          error: error.message,
          message: "Error connecting to health data"
        };
      } finally {
        // Eliminar la promesa pendiente cuando se complete
        delete apiCache.pendingPromises.healthData;
      }
    })();
 
    // Almacenar la promesa para posibles solicitudes simultáneas
    apiCache.pendingPromises.healthData = healthDataPromise;
 
    return healthDataPromise;
  } catch (error) {
    console.error('Unexpected error in connectHealthData:', error);
    return { 
      success: false, 
      error: error.message,
      message: "An unexpected error occurred"
    };
  }
};
 
// Método para desconectar datos de salud
agtfitService.disconnectHealthData = () => {
  // Eliminar datos de caché
  sessionStorage.removeItem('agtfit_health_data');
  localStorage.removeItem('agtfit_health_connected');
 
  return { success: true };
};

// Aplicar throttle a métodos críticos
agtfitService.resetBurnedCalories = throttle(agtfitService.resetBurnedCalories, 2000); // Limitar a una llamada cada 2 segundos
agtfitService.updateDayCaloriesBurned = throttle(agtfitService.updateDayCaloriesBurned, 2000); // Limitar a una llamada cada 2 segundos
 
export default agtfitService;